/**
 * Rebiller customer context in My Account (Owais, 30 Oct 2026).
 *
 * A rebiller signs in as themselves. After My Account has checked their linked customers (GET /account/rebiller/customers), the
 * chosen customer is made active here; from then on every /account and /telecom request of this page carries the header
 * X-RO-Customer-Context, and OneGate checks the link again before answering as that customer's account. Nothing here grants
 * access: an unknown or removed customer is refused by the server. The choice survives a reload (sessionStorage, per signed-in
 * user and tab) and is passed to the Billing Portal in the sign-in link (?customer=).
 * Customer organizations (Owais, 30 Oct 2026): the same list also carries the billing accounts of the person's organization
 * (via 'organization') and, when they own billing data too, their own account (via 'self', which means no context). Only
 * rebillers must choose; everyone else opens on their own account unless the address or this tab's last choice names an entry.
 * Pure functions and one module variable; tests: frontend/test/customer-context.test.cjs.
 */
import type { CustomerFigures } from '../../pages/my-account/customer-picker-view';
export const CONTEXT_HEADER = 'X-RO-Customer-Context';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export type ContextVia = 'organization' | 'rebiller' | 'self';
/** One entry of GET /account/rebiller/customers. via and permissions are absent from older answers (then: a rebiller link). */
export interface LinkedCustomer { id: string; name: string; email: string; accounts: { sourceId: string; name?: string | null; currency?: string | null }[]; via?: ContextVia; permissions?: string[];
  /** Rebiller customer selection (2 Nov 2026): status, balance and voice credit for the box; absent on older answers. */
  summary?: CustomerFigures | null; }
/** accountMode 'organization' (Owais, 2 Nov 2026): a member with no own account; they open on their organization's account. */
export interface RebillerView { type: string; organization?: { id: string; name: string; status: string } | null; customers: LinkedCustomer[]; accountMode?: 'organization'; }

let active: string | null = null;
/** How the active customer is reached (SPEC-4, 2 Nov 2026: a rebiller link covers voice billing only). */
let activeVia: ContextVia | null = null;
/** The customer every account request of this page runs as (null: the signed-in person's own account). */
export function activeCustomer(): string | null { return active; }
export function setActiveCustomer(id: string | null, via: ContextVia | null = null): void { active = id && UUID.test(id) ? id : null; activeVia = active ? via : null; }
/** How the active customer is reached (null: own account). The sidebar reads it on use: a rebiller's customer view marks "Customers". */
export function activeContextVia(): ContextVia | null { return active ? activeVia : null; }
/** The customer the Billing Portal opens on by default: none under a rebiller selection (Billing is the rebiller's own; SPEC-4). */
export function billingCustomer(): string | null { return activeVia === 'rebiller' ? null : active; }
/**
 * SPEC-4 §2 (Owais, 2 Nov 2026): under a rebiller's selected customer only voice routes run as the customer; every other account
 * request (orders, cart, one-time and licence invoices, cards, addresses, profile, logo) stays the rebiller's own.
 */
export const REBILLER_VOICE_ROUTE = /^\/(telecom\/|account\/(voice-credit|credit-payment|billing-balance|late-fees|payment-terms|summary)(\/|$))/;

/**
 * Organization roles on screen (Owais, 2 Nov 2026). While an organization account is selected, My Account shows only what the
 * member's role there allows (the server refuses the rest anyway): the selected entry's customer permissions, or null (no limit:
 * own account, rebiller links). Module state like the active customer; the sidebar reads it on use.
 */
let activePermissions: string[] | null = null;
export function activeRolePermissions(): string[] | null { return activePermissions; }
export function setActiveRolePermissions(list: string[] | null): void { activePermissions = Array.isArray(list) ? [...list] : null; }
/** The permission limit of an entry: only organization entries carry one. */
export function rolePermissionsOf(entry: LinkedCustomer | null): string[] | null {
  return entry && viaOf(entry) === 'organization' && Array.isArray(entry.permissions) ? entry.permissions : null;
}
/** The customer permission(s) a My Account tab needs under an organization role; Portals (the person's own sign-ins) never. */
const TAB_ROLE_PERMISSIONS: Record<string, string[]> = {
  overview: ['account.profile.read', 'account.invoices.read', 'account.orders.read', 'account.voice.read', 'account.billing.read', 'account.cart.read'],
  cart: ['account.cart.read'], orders: ['account.orders.read'], billing: ['account.billing.read'], invoices: ['account.invoices.read'], portals: [],
};
const WRITE_ROLE_PERMISSION: Record<string, string> = { billing: 'account.billing.write', cart: 'account.cart.write', orders: 'account.orders.write' };
export function roleAllowsTab(tab: string, permissions: string[] | null): boolean {
  return !permissions || (TAB_ROLE_PERMISSIONS[tab] || []).some(p => permissions.includes(p));
}
export function roleAllowsWrite(kind: 'billing' | 'cart' | 'orders', permissions: string[] | null): boolean {
  return !permissions || permissions.includes(WRITE_ROLE_PERMISSION[kind]);
}
export const isOrganizationOnly = (view: RebillerView | null): boolean => view?.accountMode === 'organization';
/**
 * Resellers (Owais, 2 Nov 2026): a reseller (PortaBilling customer type reseller, kept by OneGate) works exactly like a normal
 * customer; My Account only shows a "Reseller" badge, from `type` of GET /account/rebiller/customers, and only on their own account
 * (not while an organization or customer account is selected: the badge describes the signed-in person's own account).
 */
export function showsResellerBadge(view: RebillerView | null, entry: LinkedCustomer | null): boolean {
  return view?.type === 'reseller' && !contextIdOf(entry);
}

/**
 * SPEC-3 (Owais, 2 Nov 2026): two organization roles. Billing Admin orders, pays and changes addresses and cards on the organization's
 * account; View Only sees it and changes nothing. Orders, the cart and (Amendment A, later the same day) the cards belong to the
 * organization's account: every member who can see billing sees the same cards. Only the default card is per person: under an
 * organization entry `isDefault` is the signed-in person's own default, and setting it stores their preference (the account's
 * default is unchanged). Rebiller links and the own account are unchanged.
 */
export function isOrganizationEntry(entry: LinkedCustomer | null): boolean { return !!entry && viaOf(entry) === 'organization'; }
/**
 * A rebiller's customer link (Owais, 2 Nov 2026): Cart and Orders are read-only there, because OneGate refuses cart changes,
 * checkout and order actions (renewal, support ticket) under a rebiller context (customer-relationships contextAllowed).
 */
export function isRebillerEntry(entry: LinkedCustomer | null): boolean { return !!entry && viaOf(entry) === 'rebiller'; }
/** The default-card button: "Set as my default" under an organization entry (a personal preference), else "Make default". */
export function defaultCardActionLabel(entry: LinkedCustomer | null): string { return isOrganizationEntry(entry) ? 'Set as my default' : 'Make default'; }
/** "Ordered by Sam Lee" / "Ordered by sam@acme.test" when the order records who placed it; '' otherwise. */
type PlacedBy = { placedByActorEmail?: string | null; placedByActorName?: string | null } | null;
export function orderedByName(order: PlacedBy): string {
  return (String(order?.placedByActorName || '').trim() || String(order?.placedByActorEmail || '').trim()).slice(0, 120);
}
export function orderedByText(order: PlacedBy): string { const who = orderedByName(order); return who ? 'Ordered by ' + who : ''; }
/**
 * The sidebar Billing link: hidden only for an organization-only member with no account to open (no role yet, "Waiting for
 * access"); shown for Billing Admin and View Only, and for everyone else as before. Read on use like licencesHidden().
 */
export function billingLinkHidden(userId: string, store: Pick<Storage, 'getItem'> | null): boolean {
  if (active) return false;
  try { return !!userId && store?.getItem(orgOnlyKey(userId)) === '1' && !UUID.test(store?.getItem(activeKey(userId)) || ''); } catch { return false; }
}

/**
 * Only the customer's own account data carries the header; the person's own customer list and organization never do. The
 * payment-method routes keep it too (SPEC-3 §5): the server keeps the signed-in person for them and only learns the account.
 */
export function needsContext(url: string, apiBase: string, method = 'GET'): boolean {
  if (!active || !url.startsWith(apiBase)) return false;
  const [path, search = ''] = url.slice(apiBase.length).split('?');
  if (activeVia === 'rebiller') return REBILLER_VOICE_ROUTE.test(path) || rebillerMonthlyList(method, path, search);
  return /^\/(account|telecom)\//.test(path) && !/^\/account\/(rebiller|organization)(\/|$)/.test(path);
}
/**
 * Rebiller Customers view (Owais, 3 Nov 2026): the selected customer's Monthly invoices panel reads GET
 * /account/billing-invoices?tab=monthly. The same rule as the server (backend lib/customer-relationships.js rebillerVoiceRoute):
 * a GET with exactly one tab=monthly; any other tab or method stays the rebiller's own (no header).
 */
function rebillerMonthlyList(method: string, path: string, search: string): boolean {
  if (String(method || '').toUpperCase() !== 'GET' || path !== '/account/billing-invoices') return false;
  const tabs = new URLSearchParams(search).getAll('tab');
  return tabs.length === 1 && tabs[0] === 'monthly';
}

/** The customer's name. Owais, 3 Nov 2026: no voice account numbers anywhere a customer or rebiller reads (was "Acme · Account 325033"). */
export function customerOption(c: LinkedCustomer): string {
  return String(c.name || '').trim() || 'Customer';
}

/** Organization entries and the own account (Owais, 30 Oct 2026): "Your own account" names the person's own billing data. */
export function contextOption(c: LinkedCustomer): string {
  return c.via === 'self' ? customerOption({ ...c, name: 'Your own account' }) : customerOption(c);
}
const viaOf = (c: LinkedCustomer): ContextVia => c.via === 'organization' || c.via === 'self' ? c.via : 'rebiller';
/** The way an entry is reached (an older answer without via is a rebiller link); null for no entry. */
export function entryVia(c: LinkedCustomer | null): ContextVia | null { return c ? viaOf(c) : null; }
/**
 * The "Select Your Customer" dropdown: organization members with more than one entry. Rebillers never get it: they open on their
 * own account and open a customer's voice service from the Customers section (SPEC-4, 2 Nov 2026; Customers list since 3 Nov 2026).
 */
export function showsCustomerSelect(view: RebillerView | null): boolean {
  const list = view?.customers || [];
  // A rebiller who is also an organization member keeps the dropdown for the organization's accounts (SPEC-3 unchanged).
  return !!view && (view.type === 'rebiller' ? list.some(c => viaOf(c) === 'organization') : list.length > 1);
}
/** A rebiller: the Customers section and the "Your customers" panel on their own dashboard. */
export function showsCustomerPicker(view: RebillerView | null): boolean { return view?.type === 'rebiller'; }
/** The selector's options: the own account first, then "Your organization" and "Customers you manage" (empty groups left out). */
export function contextGroups(view: RebillerView | null): { own: LinkedCustomer | null; groups: { label: string; customers: LinkedCustomer[] }[] } {
  const list = view?.customers || [];
  const groups = [{ label: 'Your organization', customers: list.filter(c => viaOf(c) === 'organization') }, { label: 'Customers you manage', customers: list.filter(c => viaOf(c) === 'rebiller') }];
  return { own: list.find(c => viaOf(c) === 'self') || null, groups: groups.filter(g => g.customers.length) };
}
/** Pay buttons for the selected entry: the own account always; an entry without a permission list keeps today's behaviour. */
export function canPayFor(entry: LinkedCustomer | null): boolean {
  return !entry || viaOf(entry) === 'self' || !Array.isArray(entry.permissions) || entry.permissions.includes('account.payments.write');
}
/** The id requests run as for an entry: none for the own account. */
export function contextIdOf(entry: LinkedCustomer | null): string | null { return entry && viaOf(entry) !== 'self' ? entry.id : null; }

const key = (userId: string) => 'ro.myAccount.customer.' + userId;
/**
 * The entry to open: ?customer= on the address (a link or a reload after switching), else this tab's last choice. Only an id
 * in the current list is used. Otherwise everyone opens on their own account (the 'self' entry when listed, else null).
 * Rebillers (SPEC-4, 2 Nov 2026): a customer only from the address (the voice view); nothing is remembered, so My Account and
 * every sidebar link open on the rebiller's own account.
 */
export function chooseEntry(view: RebillerView | null, userId: string, search: string, store: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null): LinkedCustomer | null {
  const list = view?.customers || [];
  if (!view || (view.type !== 'rebiller' && !list.length)) { try { store?.removeItem(key(userId)); } catch {} return null; }
  const fromUrl = new URLSearchParams(search || '').get('customer');
  let stored: string | null = null;
  try { stored = store?.getItem(key(userId)) || null; } catch {}
  const inList = (id: string | null) => list.find(c => c.id === id) || null;
  // A rebiller link is taken from the address only; an organization entry is remembered as for any organization member.
  const pickId = [fromUrl, stored].find(id => !!id && !!inList(id) && (id === fromUrl || viaOf(inList(id)!) !== 'rebiller')) || null;
  const picked = inList(pickId);
  try { if (picked && viaOf(picked) !== 'rebiller') store?.setItem(key(userId), picked.id); else store?.removeItem(key(userId)); } catch {}
  // Organization-only members (2 Nov 2026) have no own account: they open on their organization's first account.
  if (!picked && isOrganizationOnly(view)) return list.find(c => viaOf(c) === 'organization') || null;
  return picked || list.find(c => viaOf(c) === 'self') || null;
}
/** The customer every account request runs as: the chosen entry's id, or null (own account, 'self', or nothing chosen yet). */
export function chooseCustomer(view: RebillerView | null, userId: string, search: string, store: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null): string | null {
  return contextIdOf(chooseEntry(view, userId, search, store));
}
export function rememberCustomer(userId: string, id: string, store: Pick<Storage, 'setItem'> | null): void { try { store?.setItem(key(userId), id); } catch {} }
/** Leaving a rebiller's voice view (SPEC-4): nothing is remembered for this tab, so My Account opens on the own account. */
export function forgetCustomer(userId: string, store: Pick<Storage, 'removeItem'> | null): void {
  setActiveCustomer(null);
  try { store?.removeItem(key(userId)); store?.removeItem(activeKey(userId)); } catch {}
}

/**
 * My Licences while a customer is selected (round 48, audit row 112). The License Manager signs in as the person, so it would show
 * the rebiller's own licences under the customer's name. Default: My Licences (sidebar, dashboard cards, quick link, Portals tile)
 * is hidden while a customer is selected and shown again on the own account. markActiveCustomer() records the selection for this
 * tab so a direct /sso?portal=license-portal link (a deep link or a bookmark) can be stopped with LICENCES_OWN_ACCOUNT_ONLY.
 * To change the rule later, change licencesHidden() only.
 */
export const LICENCES_OWN_ACCOUNT_ONLY = 'Licences are shown for your own account only. Switch back to your account to see them.';
const activeKey = (userId: string) => 'ro.myAccount.activeCustomer.' + userId;
export function markActiveCustomer(userId: string, id: string | null, store: Pick<Storage, 'setItem' | 'removeItem'> | null): void {
  try { if (id && UUID.test(id)) store?.setItem(activeKey(userId), id); else store?.removeItem(activeKey(userId)); } catch {}
}
/** Organization-only members (2 Nov 2026) have no own licences either: remembered for this tab like the selected customer. */
const orgOnlyKey = (userId: string) => 'ro.myAccount.organizationOnly.' + userId;
export function markOrganizationOnly(userId: string, on: boolean, store: Pick<Storage, 'setItem' | 'removeItem'> | null): void {
  try { if (on) store?.setItem(orgOnlyKey(userId), '1'); else store?.removeItem(orgOnlyKey(userId)); } catch {}
}
/**
 * Rebiller Customers section (Owais, 3 Nov 2026): the sidebar shows "Customers" for rebillers only. My Account learns the type from
 * GET /account/rebiller/customers and records it for this tab (like markOrganizationOnly); the sidebar reads it on use, so it also
 * holds on Profile and Users. Nothing here grants access: the Customers list and every customer request are checked by the server.
 */
const rebillerKey = (userId: string) => 'ro.myAccount.rebiller.' + userId;
export function markRebiller(userId: string, on: boolean, store: Pick<Storage, 'setItem' | 'removeItem'> | null): void {
  try { if (on && userId) store?.setItem(rebillerKey(userId), '1'); else store?.removeItem(rebillerKey(userId)); } catch {}
}
export function isRebillerSession(userId: string, store: Pick<Storage, 'getItem'> | null): boolean {
  try { return !!userId && store?.getItem(rebillerKey(userId)) === '1'; } catch { return false; }
}
/** True while this page (or, after a reload / new page in this tab, the last My Account page) runs as a selected customer. */
export function licencesHidden(userId: string, store: Pick<Storage, 'getItem'> | null): boolean {
  if (active && activeVia !== 'rebiller') return true; // SPEC-4: My Licences stays the rebiller's own during a voice view
  try { return !!userId && (UUID.test(store?.getItem(activeKey(userId)) || '') || store?.getItem(orgOnlyKey(userId)) === '1'); } catch { return false; }
}
/** The License Manager's audience names (the Portals tile and /sso?portal=…). */
export function isLicenseAudience(audience: unknown): boolean { return ['license-portal', 'licenseportal'].includes(String(audience || '').toLowerCase()); }

/** Billing Portal path with the selected customer, so Billing opens on the same customer (Billing checks the link again). */
export function withCustomer(path: string, customerId: string | null): string {
  if (!customerId) return path;
  const [p, hash = ''] = path.split('#');
  return p + (p.includes('?') ? '&' : '?') + 'customer=' + encodeURIComponent(customerId) + (hash ? '#' + hash : '');
}
